import { FC, memo } from 'react'
import { Box, useToken, useColorModeValue } from '@chakra-ui/react'

type Props = {
  size?: number | string
}

const PowerPlugIllustration: FC<Props> = ({ size = 60 }) => {
  const [primary300, primary500, primary700, gray50, gray900] = useToken('colors', [
    'primary.300',
    'primary.500',
    'primary.500',
    'gray.50',
    'gray.900',
  ])

  const bgColor = useColorModeValue(gray50, gray900)
  const strokeColor = useColorModeValue(primary500, primary700)
  const strokenWidth = '6px'
  const fillColor = useColorModeValue(primary300, primary700)

  return (
    <Box w={size} h={size} pos="relative">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 584.08 496.95">
        <g id="BG">
          <path
            fill={bgColor}
            d="M646.9,533.26l-32.52-22.77-4.77-3.34A232.19,232.19,0,0,0,627.5,417.5c0-128.68-104.32-233-233-233a232,232,0,0,0-107.1,26c-12.44-8.81-24.7-18.21-37.6-26.3-.19-.12-.37-.27-.56-.4-6.64-4.65-16.67-1.2-20.52,5.38-4.38,7.49-1.28,15.87,5.38,20.53l25.44,17.81a233.92,233.92,0,0,0-22.21,18l-27.61-19.34c-13.25-9.27-26.22-19.35-39.92-27.94-.19-.12-.37-.27-.56-.4-6.64-4.65-16.67-1.2-20.52,5.38-4.38,7.49-1.28,15.87,5.38,20.53l32.67,22.88,29.6,20.72a234.18,234.18,0,0,0-16.44,22c-10.17-7.34-20.29-14.91-30.88-21.52l-.54-.39c-6.64-4.65-16.67-1.2-20.52,5.39-4.38,7.48-1.28,15.86,5.38,20.52l31.45,22,.18.13a231.9,231.9,0,0,0-23.5,102c0,128.68,104.32,233,233,233a232.27,232.27,0,0,0,166.76-70.3l18.18,12.74c13.19,9.23,26.11,19.28,39.75,27.83.2.12.38.27.57.4,6.64,4.65,16.67,1.2,20.52-5.38,4.38-7.49,1.28-15.87-5.38-20.53l-32.52-22.77L580.83,557.4a234.37,234.37,0,0,0,15.33-23.12c11.57,8.27,23,17,35,24.49.2.12.38.27.57.4,6.64,4.65,16.67,1.2,20.52-5.38C656.66,546.3,653.56,537.92,646.9,533.26Z"
            transform="translate(-97.05 -154.05)"
          />
          <path
            fill={bgColor}
            d="M136.22,247.12l-16.71-11.7c-6.45-4.51-17-1.38-20.52,5.39-4.06,7.68-1.5,15.7,5.38,20.52L121.08,273c6.44,4.51,17,1.38,20.52-5.38C145.66,260,143.1,251.94,136.22,247.12Z"
            transform="translate(-97.05 -154.05)"
          />
        </g>
        <g id="Stars">
          <g id="Star">
            <path
              fill={strokeColor}
              d="M274.5,507h20a1.5,1.5,0,0,0,0-3h-20a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-96.23 -154.05)"
            />
            <path
              fill={strokeColor}
              d="M283,495.5v20a1.5,1.5,0,0,0,3,0v-20a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-96.23 -154.05)"
            />
          </g>
          <g id="Star-2" data-name="Star">
            <path
              fill={strokeColor}
              d="M556.5,347h16a1.5,1.5,0,0,0,0-3h-16a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-96.23 -154.05)"
            />
            <path
              fill={strokeColor}
              d="M563,337.5v16a1.5,1.5,0,0,0,3,0v-16a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-96.23 -154.05)"
            />
          </g>
          <g id="Star-3" data-name="Star">
            <path
              fill={strokeColor}
              d="M418.5,237h12a1.5,1.5,0,0,0,0-3h-12a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-96.23 -154.05)"
            />
            <path
              fill={strokeColor}
              d="M423,229.5v12a1.5,1.5,0,0,0,3,0v-12a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-96.23 -154.05)"
            />
          </g>
          <g id="Star-4" data-name="Star">
            <path
              fill={strokeColor}
              d="M529.5,627h10a1.5,1.5,0,0,0,0-3h-10a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-96.23 -154.05)"
            />
            <path
              fill={strokeColor}
              d="M533,620.5v10a1.5,1.5,0,0,0,3,0v-10a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-96.23 -154.05)"
            />
          </g>
          <g id="Star-5" data-name="Star">
            <path
              fill={strokeColor}
              d="M369.5,597h10a1.5,1.5,0,0,0,0-3h-10a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-96.23 -154.05)"
            />
            <path
              fill={strokeColor}
              d="M373,590.5v10a1.5,1.5,0,0,0,3,0v-10a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-96.23 -154.05)"
            />
          </g>
          <g id="Star-6" data-name="Star">
            <path
              fill={strokeColor}
              d="M633.5,447h22a1.5,1.5,0,0,0,0-3h-22a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-96.23 -154.05)"
            />
            <path
              fill={strokeColor}
              d="M643,434.5v22a1.5,1.5,0,0,0,3,0v-22a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-96.23 -154.05)"
            />
          </g>
          <g id="Star-7" data-name="Star">
            <path
              fill={strokeColor}
              d="M135.5,467h18a1.5,1.5,0,0,0,0-3h-18a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-96.23 -154.05)"
            />
            <path
              fill={strokeColor}
              d="M143,456.5v18a1.5,1.5,0,0,0,3,0v-18a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-96.23 -154.05)"
            />
          </g>
          <g id="Star-8" data-name="Star">
            <path
              fill={strokeColor}
              d="M304.5,167h20a1.5,1.5,0,0,0,0-3h-20a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-96.23 -154.05)"
            />
            <path
              fill={strokeColor}
              d="M313,155.5v20a1.5,1.5,0,0,0,3,0v-20a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-96.23 -154.05)"
            />
          </g>
          <g id="Star-9" data-name="Star">
            <path
              fill={strokeColor}
              d="M282.5,307h24a1.5,1.5,0,0,0,0-3h-24a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-96.23 -154.05)"
            />
            <path
              fill={strokeColor}
              d="M293,293.5v24a1.5,1.5,0,0,0,3,0v-24a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-96.23 -154.05)"
            />
          </g>
        </g>
        <g id="Femea">
          <path
            d="M521.66,463.21s24.63,10.18,33.18,20.44,12.84,22.29,13.69,30.81c1.71,17.11-1.71,25.67-3.42,30.8S558.27,559,551.42,565.8c-6,6.05-18.82,13.69-25.67,17.11s-12,5.13-13.69,1.71-1.71-8.55,3.42-10.27,18.83-10.26,25.67-17.11a65.22,65.22,0,0,0,13.69-20.53c3.43-8.56,3.43-20.54,0-30.81-2.42-7.26-12-18.82-17.11-22.25-7.67-5.11-16.16-8.74-18.35-8.65"
            transform="translate(-97.05 -154.05)"
            stroke={strokeColor}
            strokeWidth={strokenWidth}
            fill={fillColor}
          />
          <path
            d="M509.49,450.28s8.9,1,11.13,7.7c1.71,5.14,1.71,6.85,0,13.7-1.32,5.25-5.14,8.55-10.27,10.26-4.87,1.63-7.7.29-7.7.29"
            transform="translate(-97.05 -154.05)"
            stroke={strokeColor}
            strokeWidth={strokenWidth}
            fill={fillColor}
          />
          <path
            stroke={strokeColor}
            strokeWidth={strokenWidth}
            fill={'white'}
            d="M457.3,415.2s22.25,3.42,32.51,10.27S506.63,441,508.64,446c3.42,8.55,1.71,17.11,0,23.95-.93,3.72-5.42,13.18-12,17.12-8.56,5.13-18.91,9.83-29.09,8.55-13.69-1.71-29.1-8.55-29.1-8.55"
            transform="translate(-97.05 -154.05)"
          />
          <path
            stroke={strokeColor}
            strokeWidth={strokenWidth}
            fill={'white'}
            d="M429.19,476.24l9,10.51a.29.29,0,0,0,.49,0c1.58-2.79,11.65-20.87,15.2-33.86,4.95-18.15,3.53-36.31,3.42-37.58,0,0,0,0-.07,0l-15.07,6a4.12,4.12,0,0,0-2.49,2.85c-2.11,8.49-10.05,40.74-11.3,49.32A3.49,3.49,0,0,0,429.19,476.24Z"
            transform="translate(-97.05 -154.05)"
          />
          <ellipse
            fill={fillColor}
            cx="439.33"
            cy="463.12"
            rx="8.66"
            ry="4.23"
            transform="translate(-194.9 653.92) rotate(-79.21)"
          />
          <ellipse
            fill={fillColor}
            cx="446.17"
            cy="437.45"
            rx="8.66"
            ry="4.23"
            transform="translate(-164.12 639.78) rotate(-79.21)"
          />
          <path
            fill={fillColor}
            d="M470.16,484.36a29.76,29.76,0,0,0,15-2.91,69.24,69.24,0,0,0,10.66-7.75c.51-.42,1-.85,1.6-1.25l.25.16a16.64,16.64,0,0,1-7.75,9.41,29.83,29.83,0,0,1-11.79,3.33,27.85,27.85,0,0,1-8-.69l.06-.3Z"
            transform="translate(-97.05 -154.05)"
          />
        </g>
        <g id="Macho">
          <path
            stroke={strokeColor}
            strokeWidth={strokenWidth}
            fill={fillColor}
            d="M259.33,399s-24.62-10.17-33.18-20.44-12.84-22.29-13.69-30.8c-1.71-17.11,1.71-25.67,3.42-30.81s6.85-13.69,13.69-20.53c6.05-6.05,18.83-13.69,25.67-17.11s12-5.14,13.69-1.72,1.71,8.56-3.42,10.27S246.69,298.12,239.84,305a65.28,65.28,0,0,0-13.69,20.53c-3.42,8.56-3.42,20.54,0,30.8,2.42,7.27,12,18.83,17.11,22.25,7.67,5.11,16.17,8.75,18.35,8.65"
            transform="translate(-97.05 -154.05)"
          />
          <path
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth={strokenWidth}
            d="M271.5,411.92s-8.89-1-11.12-7.7c-1.71-5.13-1.71-6.84,0-13.69,1.31-5.25,5.13-8.56,10.26-10.27,4.88-1.62,7.71-.28,7.71-.28"
            transform="translate(-97.05 -154.05)"
          />
          <path
            stroke={strokeColor}
            strokeWidth={strokenWidth}
            fill={'white'}
            d="M323.7,447s-22.25-3.42-32.52-10.26-16.81-15.51-18.82-20.54c-3.43-8.56-1.72-17.11,0-24,.92-3.71,5.41-13.17,12-17.11,8.55-5.13,18.9-9.83,29.09-8.56,13.69,1.71,29.09,8.56,29.09,8.56"
            transform="translate(-97.05 -154.05)"
          />
          <path
            stroke={strokeColor}
            strokeWidth={strokenWidth}
            fill={'white'}
            d="M351.81,386l-9-10.5a.3.3,0,0,0-.49,0c-1.57,2.78-11.65,20.87-15.19,33.86-4.95,18.15-3.54,36.3-3.43,37.57,0,0,0,.06.08.05l15.07-6a4.15,4.15,0,0,0,2.48-2.85c2.11-8.5,10.05-40.75,11.3-49.33A3.54,3.54,0,0,0,351.81,386Z"
            transform="translate(-97.05 -154.05)"
          />
          <path
            fill={fillColor}
            d="M310.83,377.84a29.89,29.89,0,0,0-15,2.91,68.73,68.73,0,0,0-10.65,7.76c-.51.42-1,.85-1.61,1.25l-.25-.17a16.66,16.66,0,0,1,7.75-9.4,29.76,29.76,0,0,1,11.8-3.33,27.43,27.43,0,0,1,8,.69l-.06.29Z"
            transform="translate(-97.05 -154.05)"
          />
          <g>
            <path
              stroke={strokeColor}
              strokeWidth={strokenWidth}
              fill={'white'}
              d="M381.88,445.59l-46.15-13.36c-3.5-1-2.39-3.82-1.41-6.8h0c1-3,1.5-5,5-4l46.15,13.36C389,435.78,391,439,390,442h0C389,445,385.38,446.61,381.88,445.59Z"
              transform="translate(-97.05 -154.05)"
            />
            <path
              fill={fillColor}
              d="M333.66,419.14l-3.32,10.77-.48,1.57a2.27,2.27,0,0,0,1.57,2.76,2.29,2.29,0,0,0,2.77-1.57l3.31-10.77.49-1.56a2.25,2.25,0,0,0-4.34-1.2Z"
              transform="translate(-97.05 -154.05)"
            />
          </g>
          <g>
            <path
              stroke={strokeColor}
              strokeWidth={strokenWidth}
              fill={'white'}
              d="M388.52,417.13l-46.15-13.37c-3.49-1-2.39-3.82-1.4-6.79h0c1-3,1.49-5.05,5-4l46.15,13.36c3.49,1,5.55,4.27,4.56,7.25h0C395.68,416.53,392,418.14,388.52,417.13Z"
              transform="translate(-97.05 -154.05)"
            />
            <path
              fill={fillColor}
              d="M340.3,390.67,337,401.44,336.5,403a2.25,2.25,0,0,0,4.34,1.2l3.32-10.77.48-1.57a2.28,2.28,0,0,0-1.57-2.77,2.31,2.31,0,0,0-2.77,1.57Z"
              transform="translate(-97.05 -154.05)"
            />
          </g>
        </g>
      </svg>
    </Box>
  )
}

export default memo(PowerPlugIllustration)
